import React from 'react';
import { useIntl } from 'react-intl';
import * as Types from '@xingternal/pages-graphql-types';

import { FeatureSwitch } from '@xingternal/feature-switch';

import {
  FEATURE_SWITCH_ENABLE_DRAFT_SCHEDULE,
} from '../constants';
import { publicationStateIndex } from '../utils';
import { trackNewsPublicationFilter } from '@xingternal/pages-tracking';
import { SubpageNavigation } from '@xingternal/pages-common';
import { useEntityPageFeedQuery, EntityPageFeedQueryVariables } from '../FeedQuery.gql-types';
import FEED_QUERY from '../FeedQuery.graphql';

import styles from './News.css';

type NewsNavigationProps = {
  feedQueryVars: EntityPageFeedQueryVariables,
  publicationState?: Types.PostingPublicationState | null,
  setPublicationState: (publicationState: Types.PostingPublicationState) => void,
  isLoading: boolean,
  pageId: string,
  metadata: Types.EntityPageContentFeedCollectionMetadata,
};

const NewsNavigation = ({
  setPublicationState,
  publicationState,
  feedQueryVars,
  isLoading,
  pageId,
  metadata,
}: NewsNavigationProps) => {
  const intl = useIntl();
  const selectPublicationState = (state: Types.PostingPublicationState) => {
    if (state !== publicationState && !isLoading) {
      setPublicationState(state);
      trackNewsPublicationFilter(state, pageId);
    }
  };

  const { refetch } = useEntityPageFeedQuery({
    // @ts-ignore TODO: delete query field, does not exist in this function
    query: FEED_QUERY,
    variables: { ...feedQueryVars },
    fetchPolicy: 'network-only',
  });

  const visibleDefaultTabs = [
    {
      copy: intl.formatMessage({
        id: 'NEWS_MODULE_PUBLISHED_TAB',
      }),
      onClick: () => {
        refetch();
        selectPublicationState(Types.PostingPublicationState.Published);
      },
      disabled: isLoading,
      'data-testid': 'PUBLISHED_TAB',
    },
    {
      copy: `${intl.formatMessage({
        id: 'NEWS_MODULE_HIDDEN_TAB',
      })} (${metadata.hiddenTotal})`,
      onClick: () => {
        refetch();
        selectPublicationState(Types.PostingPublicationState.Hidden);
      },
      disabled: isLoading || metadata.hiddenTotal === 0,
      'data-testid': 'HIDDEN_TAB',
    },
  ];

  const featureSwitchTabs = [
    visibleDefaultTabs[0],
    {
      copy: `${intl.formatMessage({
        id: 'NEWS_MODULE_SCHEDULED_TAB',
      })} (${metadata.scheduledTotal})`,
      onClick: () => {
        refetch();
        selectPublicationState(Types.PostingPublicationState.Scheduled);
      },
      disabled: isLoading || metadata.scheduledTotal === 0,
      'data-testid': 'SCHEDULED_TAB',
    },
    {
      copy: `${intl.formatMessage({
        id: 'NEWS_MODULE_DRAFT_TAB',
      })} (${metadata.draftTotal})`,
      onClick: () => {
        refetch();
        selectPublicationState(Types.PostingPublicationState.Draft);
      },
      disabled: isLoading || metadata.draftTotal === 0,
      'data-testid': 'DRAFT_TAB',
    },
    visibleDefaultTabs[1],
  ];

  const filterProps = {
    intl,
    activeItemIndex: publicationStateIndex(publicationState),
    allowDropdown: false,
  };

  return (
    <div className={styles.newsNavigation} data-testid={'NEWS_NAVIGATION'}>
      <FeatureSwitch
        flags={FEATURE_SWITCH_ENABLE_DRAFT_SCHEDULE}
        elseCase={
          <SubpageNavigation
            {...filterProps}
            variant={'filled'}
            size={'small'}
            data={visibleDefaultTabs}
            noPadding
          />
        }
      >
        <SubpageNavigation
          {...filterProps}
          variant={'filled'}
          size={'small'}
          data={featureSwitchTabs}
          activeItemIndex={publicationStateIndex(publicationState, true)}
          noPadding
        />
      </FeatureSwitch>
    </div>
  );
};

export default NewsNavigation;
